import React, { Component, ReactNode, ErrorInfo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getFullPath } from '../../utils/pathUtils';

interface ErrorBoundaryFallbackProps {
  children: ReactNode;
  onAuthError?: (message: string) => void;
}

interface ErrorBoundaryFallbackState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundaryFallback extends Component<
  ErrorBoundaryFallbackProps,
  ErrorBoundaryFallbackState
> {
  constructor(props: ErrorBoundaryFallbackProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryFallbackState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error('Caught error:', error, errorInfo);

    // Check for authentication errors
    if (
      error.message?.includes('jwt expired') ||
      error.message?.includes('not authenticated') ||
      error.message?.includes('authentication required')
    ) {
      // Handle auth errors by redirecting to login
      localStorage.removeItem('token');
      sessionStorage.setItem('authError', 'Your session has expired. Please log in again.');
      window.location.href = getFullPath('/login');
    }
  }

  render() {
    if (this.state.hasError) {
      // Generic error UI
      return (
        <div className="flex justify-center items-center min-h-screen">
          <div className="bg-core-dark p-8 max-w-lg text-center rounded shadow-lg">
            <h1 className="text-2xl font-semibold text-error mb-4">Something went wrong</h1>
            <p className="mb-6 text-white">
              Sorry, an unexpected error has occurred. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary text-onfill rounded font-medium hover:bg-primary-medium transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component that can use hooks
interface ErrorBoundaryProps {
  children: ReactNode;
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  const { logout } = useAuth();

  const handleAuthError = (message: string) => {
    logout(message);
  };

  return <ErrorBoundaryFallback onAuthError={handleAuthError}>{children}</ErrorBoundaryFallback>;
}
